import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  selectAllCompanyUsersById,
  selectConfigCatFlag,
  selectCurrentUserAccountId,
  selectGetAllTeamsStatus,
  selectIsCurrentUserAdmin,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import ManageTeamScanModal from "app/components/Modals/ManageTeamScanModal";
import {
  getPendingTests,
  getSuperpowersAndHiddenStrengths,
  getWorkPlaceInsightsReportByUserId,
  selectGetPendingTestsStatus,
} from "app/components/WorkplaceInsightsReport/slice";
import ShortcutsCard from "../Cards/ShortcutsCard";
import { getManagerAndDirectReports } from "app/containers/AdvancedCsvUpload/slice";
import SubordinatesWeeklyCheckinSnapshot from "app/containers/WeeklyCheckInMonitor/Snapshotboxes/SubordinatesWeeklyCheckinSnapshot";
import { CONFIG_CAT_FLAGS } from "app/components/GlobalNavbar/constants";
import {
  getCompanyWeeklyCheckInCustomList,
  getWeeklyCheckInCompanySettings,
  selectGetCompanyWeeklyCheckInCustomListStatus,
  selectGetWeeklyCheckInCompanySettingsStatus,
  selectUsersSubordinatesIds,
  selectWeeklyCheckInCompanySettings,
  selectWeeklyCheckInCustomList,
} from "app/containers/WeeklyCheckInMonitor/slice";
import WeeklyCheckInWelcomeCard from "app/components/WeeklyCheckInComponents/WeeklyCheckInWelcomeCard";
import {
  shouldUserSeeWeeklyCheckInMerchandising,
  shouldWeeklyCheckInDashboardBeShown,
} from "../helpers";
import {
  selectAllUserSubordinates,
  selectGetUserSubordinatesStatus,
} from "app/containers/UserSettings/slice";
import { selectTeamAndDepartmentLeadIdsForLoggedInUser } from "app/containers/AdminConsole/slice";

export default function MemberDashboardV2() {
  const dispatch = useAppDispatch();

  // ------------------------------- Selectors --------------------------------------------- //
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const loggedInUserAccountId = useAppSelector(selectCurrentUserAccountId);
  const teamsById = useAppSelector(selectTeamsByTeamId);
  const getAllTeamsStatus = useAppSelector(selectGetAllTeamsStatus);

  const getPendingTestsStatus = useAppSelector(selectGetPendingTestsStatus);
  const weeklyCheckInConfigCatFlag = useAppSelector(
    selectConfigCatFlag(CONFIG_CAT_FLAGS.weeklyCheckIn.savedFlagName)
  );
  const weeklyCheckInCompanySettings = useAppSelector(
    selectWeeklyCheckInCompanySettings
  );
  const getWeeklyCheckinCompanySettingsStatus = useAppSelector(
    selectGetWeeklyCheckInCompanySettingsStatus
  );
  const getWeeklyCheckinCustomListStatus = useAppSelector(
    selectGetCompanyWeeklyCheckInCustomListStatus
  );
  const isAdmin = useAppSelector(selectIsCurrentUserAdmin);
  const usersSubordinatesIds = useAppSelector(selectUsersSubordinatesIds);
  const allUsersSubordinates = useAppSelector(selectAllUserSubordinates);
  const weeklyCheckInCustomList = useAppSelector(selectWeeklyCheckInCustomList);
  const getUserSubordinatesStatus = useAppSelector(
    selectGetUserSubordinatesStatus
  );
  const teamAndDepartmentLeaderOf = useAppSelector(
    selectTeamAndDepartmentLeadIdsForLoggedInUser
  );

  const isWeeklyCheckInFeatureEnabled = !!(
    weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabledForEntireCompany ||
    weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabled
  );

  // ------------------------------------- Component States ------------------------------------- //
  const [isManageTeamScanModalOpen, setIsManageTeamScanModalOpen] =
    useState(false);
  const [manageTeamScanModalTeams, setManageTeamScanModalTeams] = useState<
    number[] | null
  >(null);

  // ------------------------------------- Effect ------------------------------------- //
  useEffect(() => {
    if (!loggedInUserAccountId) return;
    dispatch(getWorkPlaceInsightsReportByUserId(loggedInUserAccountId));
    dispatch(getSuperpowersAndHiddenStrengths(loggedInUserAccountId));
    dispatch(
      getManagerAndDirectReports({ userAccountId: loggedInUserAccountId })
    );
    dispatch(getWeeklyCheckInCompanySettings());
    dispatch(
      getCompanyWeeklyCheckInCustomList({ fetchManagerSubordinates: true })
    );
  }, [loggedInUserAccountId, dispatch]);

  useEffect(() => {
    if (getPendingTestsStatus === "idle") {
      dispatch(getPendingTests());
    }
  }, [getPendingTestsStatus, dispatch]);

  // ------------------------------------- Memos ------------------------------------- //
  const userInfo = useMemo(() => {
    return loggedInUserAccountId ? usersById[loggedInUserAccountId] : null;
  }, [loggedInUserAccountId, usersById]);
  const userHasSubordinates = useMemo(() => {
    const subordinateLength = usersSubordinatesIds?.length ?? 0;
    return subordinateLength > 1;
  }, [usersSubordinatesIds]);

  const shouldWeeklyCheckInDashboardBeShownValue = useMemo(() => {
    return shouldWeeklyCheckInDashboardBeShown(
      userInfo?.userAccountId ?? 0,
      weeklyCheckInCompanySettings,
      weeklyCheckInCustomList,
      weeklyCheckInConfigCatFlag,
      allUsersSubordinates,
      teamsById
    );
  }, [
    weeklyCheckInCompanySettings,
    weeklyCheckInCustomList,
    weeklyCheckInConfigCatFlag,
    allUsersSubordinates,
    teamsById,
    userInfo?.userAccountId,
  ]);

  const userTeams = useMemo(() => {
    if (!userInfo?.teamIds) return [];
    // Filter out any teamIds that do not exist in the teamsById object
    return userInfo.teamIds.filter((teamId) => teamsById[teamId]);
  }, [teamsById, userInfo]);

  const getMerchandisingType = () => {
    if (isAdmin) {
      return "admin";
    }
    if (!userHasSubordinates) return "individual-contributor";
    return "manager";
  };

  const isWeeklyCheckInMerchandisingLoading = () => {
    if (
      getWeeklyCheckinCompanySettingsStatus !== "succeeded" ||
      getWeeklyCheckinCustomListStatus !== "succeeded"
    ) {
      return true;
    }

    if (
      weeklyCheckInCompanySettings?.hasWeeklyCheckInsEnabledForEntireCompany // if this is true no need to wait for teams and subordinates
    ) {
      return false;
    }

    if (
      getAllTeamsStatus !== "succeeded" ||
      getUserSubordinatesStatus === "loading"
    ) {
      return true;
    }

    return false;
  };

  const getWeeklyCheckInSection = () => {
    if (!weeklyCheckInConfigCatFlag || isWeeklyCheckInMerchandisingLoading()) {
      return null;
    }

    if (
      isWeeklyCheckInFeatureEnabled &&
      shouldWeeklyCheckInDashboardBeShownValue
    ) {
      return <SubordinatesWeeklyCheckinSnapshot />;
    }

    if (
      shouldUserSeeWeeklyCheckInMerchandising(
        weeklyCheckInCompanySettings,
        userInfo,
        allUsersSubordinates,
        teamAndDepartmentLeaderOf
      )
    ) {
      return <WeeklyCheckInWelcomeCard type={getMerchandisingType()} />;
    }

    return null;
  };

  return (
    <div className="dashboard-container">
      <ManageTeamScanModal
        show={isManageTeamScanModalOpen}
        onHide={() => {
          setIsManageTeamScanModalOpen(false);
          setManageTeamScanModalTeams(null);
        }}
        teams={manageTeamScanModalTeams || userTeams}
      />
      {getWeeklyCheckInSection()}
      <ShortcutsCard />
    </div>
  );
}
